import { auth } from "@app/configs/firebase.js";
import storage from "@app/configs/storage.js";
import { ref, getDownloadURL } from "firebase/storage";
import { authState } from "@app/store/authStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import decode from "@cclib/functions/decode.js";
import defaults from "@cclib/lib/defaults.js";
import formats from "@cclib/lib/formats.js";
import encodeHtml from "@cclib/functions/eventGroups/encodeHtml.js";
import fixOverlap from "@cclib/functions/eventGroups/fixOverlap.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import { getImportSettings } from "@app/lib/importSettingsPrompt.js";
import { describeMediaFetchError } from "@app/lib/media/sourceFile.js";
import * as m from "@app/paraglide/messages.js";

function getTranscriptStoragePath() {
    const userId = auth.currentUser.uid;
    const teamId = authState.team ? authState.team.id : null;
    return authState.team
        ? `teams/${teamId}/transcripts/`
        : `users/${userId}/transcripts/`;
}

function getEventGroupDefaults() {
    try {
        return JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
    } catch (err) {
        console.error(err);
        return {};
    }
}

function isEmptyPlaceholderProject() {
    if (eventGroupState.length !== 1) {
        return false;
    }

    const events = eventGroupState[0]?.events || [];
    if (events.length !== 1) {
        return false;
    }

    const event = events[0];
    const text = convertToPlainText(event.text || "").trim();
    return event.start === 2 && event.end === 4 && text === "";
}

function applyImportedGroupDefaults(transcriptEventGroup, importSettings) {
    const eventGroupDefaults = getEventGroupDefaults();
    transcriptEventGroup.type = "subtitle";
    transcriptEventGroup.maxCps = eventGroupDefaults.maxCps || 9999;
    transcriptEventGroup.maxWpm = eventGroupDefaults.maxWpm || 9999;
    transcriptEventGroup.maxChars = importSettings.maxChars;
    transcriptEventGroup.maxLines = importSettings.maxLines;
    transcriptEventGroup.minDuration = eventGroupDefaults.minDuration || 0.5;
    transcriptEventGroup.maxDuration = eventGroupDefaults.maxDuration || 12;
    transcriptEventGroup.overlap = eventGroupDefaults.overlap;
    transcriptEventGroup.illegalChars = eventGroupDefaults.illegalChars;
    if (eventGroupDefaults.fontFamily) {
        transcriptEventGroup.fontFamily = eventGroupDefaults.fontFamily;
    }
    if (eventGroupDefaults.fontSize) {
        transcriptEventGroup.fontSize = eventGroupDefaults.fontSize;
    } else if (!transcriptEventGroup.fontSize) {
        transcriptEventGroup.fontSize = 16;
    }
    return transcriptEventGroup;
}

function decodeFormatOptions(importSettings, extra = []) {
    return [
        {
            name: "Import Type",
            type: "list",
            values: ["subtitle", "transcription"],
            selected: "subtitle",
        },
        {
            name: "Max Characters",
            type: "number-input",
            values: "",
            selected: importSettings.maxChars,
        },
        {
            name: "Max Lines",
            type: "number-input",
            values: "",
            selected: importSettings.maxLines,
        },
        ...extra,
    ];
}

async function decodeTranscript(resJson, srcProfile, importSettings, extraOptions = []) {
    const options = new defaults.options({
        profile: formats.profileMapping[srcProfile].name,
        formatOptions: decodeFormatOptions(importSettings, extraOptions),
        frameRate: projectState.frameRate,
        dropFrame: projectState.dropFrame,
    });

    let transcriptEventGroup = {
        ...(await decode(JSON.stringify(resJson), options)),
    };
    transcriptEventGroup = encodeHtml(transcriptEventGroup);
    transcriptEventGroup = fixOverlap(
        transcriptEventGroup,
        editorState.minFrameGap,
        projectState.frameRate,
    );
    return applyImportedGroupDefaults(transcriptEventGroup, importSettings);
}

function resolveImportedEventGroupName(job, type, importSettings) {
    return (
        importSettings.eventGroupName?.trim() ||
        job.projectName ||
        m.ai_event_group_name({ type, index: eventGroupState.length + 1 })
    );
}

function commitMainTranscriptGroup(transcriptEventGroup) {
    if (isEmptyPlaceholderProject()) {
        eventGroupState.splice(0, 1, { ...transcriptEventGroup });
        projectState.selected = 0;
    } else {
        eventGroupState.push({ ...transcriptEventGroup });
        projectState.selected = eventGroupState.length - 1;
    }

    (transcriptEventGroup.speakers || []).forEach((sp) => {
        speakerState.push(sp);
    });
}

export async function getTranscriptFile(job) {
    const storagePath = getTranscriptStoragePath();
    const url = await getDownloadURL(ref(storage, storagePath + job.id + ".json"));
    try {
        const res = await fetch(url);
        if (!res.ok) {
            throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }
        return await res.json();
    } catch (error) {
        throw new Error(describeMediaFetchError(error, url));
    }
}

export async function importJobTranscript(job, type, importSettings) {
    const resJson = await getTranscriptFile(job);
    const srcProfile = job.config.provider;

    if (srcProfile === "Speechmatics" || srcProfile === "ElevenLabs Scribe" || srcProfile === "Gladia") {
        let transcriptEventGroup = await decodeTranscript(
            resJson,
            srcProfile,
            importSettings,
        );
        transcriptEventGroup.name = resolveImportedEventGroupName(
            job,
            type,
            importSettings,
        );
        commitMainTranscriptGroup(transcriptEventGroup);

        const translationLanguages =
            srcProfile === "Gladia"
                ? (resJson.result?.translation?.results || [])
                      .filter((result) => result.utterances?.length > 0)
                      .map((result) => result.languages?.[0])
                      .filter(Boolean)
                : Object.keys(resJson.translations || {}).filter(
                      (lang) => resJson.translations[lang].length > 0,
                  );

        if (translationLanguages.length > 0) {
            for (const lang of translationLanguages) {
                transcriptEventGroup = await decodeTranscript(
                    resJson,
                    srcProfile,
                    importSettings,
                    [
                        {
                            name: "Import Target",
                            type: "list",
                            values: ["Translation", "Transcript", "Audio Events"],
                            selected: "Translation",
                        },
                        {
                            name: "Language Code",
                            type: "text-input",
                            values: "",
                            selected: lang,
                        },
                    ],
                );
                transcriptEventGroup.name = m.machine_translation_name({
                    language: lang,
                });
                eventGroupState.push(transcriptEventGroup);
                (transcriptEventGroup.speakers || []).forEach((sp) => {
                    speakerState.push(sp);
                });
            }
        }

        if (srcProfile === "ElevenLabs Scribe") {
            const wordsArray =
                resJson.words || resJson?.transcription?.words || [];
            resJson.audio_events = wordsArray.filter((word) => {
                return word.type === "audio_event";
            });

            if (resJson.audio_events.length === 0) {
                resJson.audio_events = null;
            }
        }

        if (resJson.audio_events) {
            transcriptEventGroup = await decodeTranscript(
                resJson,
                srcProfile,
                importSettings,
                [
                    {
                        name: "Import Target",
                        type: "list",
                        values: ["Translation", "Transcript", "Audio Events"],
                        selected: "Audio Events",
                    },
                ],
            );
            transcriptEventGroup.name = m.audio_events_name();
            eventGroupState.push(transcriptEventGroup);
        }
    } else {
        const transcriptEventGroup = await decodeTranscript(
            resJson,
            srcProfile,
            importSettings,
        );
        transcriptEventGroup.name = resolveImportedEventGroupName(
            job,
            type,
            importSettings,
        );
        commitMainTranscriptGroup(transcriptEventGroup);
    }
}

export async function autoImportCompletedTranscript(job) {
    const settings = getImportSettings();
    await importJobTranscript(job, "subtitle", {
        maxChars: settings.maxChars,
        maxLines: settings.maxLines,
        eventGroupName: job.projectName || "",
    });

    insertHistoryAction({
        name: "import transcript",
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });
}
